/**
 * Viewport queries for layout-tree swaps.
 *
 * The shell renders a genuinely different tree per breakpoint — a persistent
 * resizable rail on desktop, an off-canvas drawer below it — rather than
 * CSS-hiding one variant. A drawer and a docked rail have different focus
 * management, different ARIA and different state; expressing that as
 * `display: none` ships both trees to the accessibility tree at once.
 *
 * Breakpoints follow ux-design/responsive.md:
 *   phone            0–639
 *   tablet-portrait  640–1023
 *   tablet-landscape 1024–1279
 *   desktop          1280+
 */
import { useEffect, useState } from 'react';

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const list = window.matchMedia(query);
    setMatches(list.matches);
    const onChange = (event: MediaQueryListEvent): void => setMatches(event.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** Desktop: the sidebar is docked, resizable and collapsible. */
export const useIsDesktop = (): boolean => useMediaQuery('(min-width: 1280px)');

/** Phone: the bottom tab bar carries the five primary destinations. */
export const useIsPhone = (): boolean => useMediaQuery('(max-width: 639px)');
